import { Component, OnInit, Inject } from '@angular/core';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { ChannelsDialogData } from './interfaces/channels';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { ChannelsResponse } from 'src/app/core/services/channels.service';
import { LogoLibraryResponse } from 'src/app/core/interfaces/logoLibraryResponse';
import { ChannelsService } from 'src/app/core/services/channels.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EpisodesCalendarViewComponent } from '../../extended-program-guide/episodes-calendar-view/episodes-calendar-view.component';
import { DialogService } from '../../../../shared/secured-shared/dialog/dialog.service';
import { compareFieldsToUpdate } from 'src/app/core/utils/generic-utils';

@Component({
    selector: 'app-channels-dialog',
    templateUrl: './channels-dialog.component.html',
    styleUrls: ['./channels-dialog.component.scss'],
    standalone: false
})
export class ChannelsDialogComponent implements OnInit {
  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<ChannelsDialogComponent>,
    private channelService: ChannelsService,
    private _snackBar: MatSnackBar,
    private epgDialog: DialogService,
    @Inject(MAT_DIALOG_DATA) public data: ChannelsDialogData
  ) {}

  loadingData = false;
  selectedLogo: LogoLibraryResponse;
  oldChannel: ChannelsResponse;

  channelForm = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    stream_source: new FormControl('', [
      Validators.required,
      Validators.maxLength(1000),
      (c) => this.addressValidator(c)
    ])
  });

  ngOnInit(): void {
    if (this.data.code !== 'delete-channel') {
      this.setChannelInfo(this.data.channel);
    }

    if (this.data.channel.is_external && this.data.code === 'edit-channel') {
      this.channelForm.get('name').disable();
    }
  }

  getTitle() {
    const titles = {
      'add-channel': 'Add Station',
      'edit-channel': 'Edit Station',
      'delete-channel': 'Delete Station'
    };
    return titles[this.data.code];
  }

  closeDialog(msg: string): void {
    this.dialogRef.close(msg);
  }

  save() {
    this.loadingData = true;
    if (this.data.code == 'add-channel') {
      this.createChannel();
    }
    if (this.data.code == 'edit-channel') {
      this.editChannel();
    }
  }

  createChannel() {
    this.loadingData = true;
    const newChannel: ChannelsResponse = {
      name: this.channelForm.get('name').value,
      logo_id: this.selectedLogo.id,
      stream_source: this.channelForm.get('stream_source').value,
      provider: 'Internal',
      is_external: false
    };

    this.channelService.createChannel(newChannel).subscribe({
      next: () => {
        this.loadingData = false;
        this.closeDialog('channel-saved');
      },
      error: (error) => {
        this.loadingData = false;
        this.openSnackBar(error.message, 'Ok');
      }
    });
  }

  editChannel() {
    this.loadingData = true;
    const id = this.data.channel.id;
    const newChannel = {
      ...this.channelForm.value,
      logo_id: this.selectedLogo.id
    };

    let fieldsToUpdate: ChannelsResponse;

    if (this.data.channel.is_external) {
      fieldsToUpdate = {
        stream_source: newChannel.stream_source
      };
    } else {
      fieldsToUpdate = compareFieldsToUpdate(newChannel, this.oldChannel);
    }

    this.channelService.editChannel(id, fieldsToUpdate).subscribe({
      next: () => {
        this.loadingData = false;
        this.closeDialog('channel-edited');
      },
      error: (error) => {
        this.loadingData = false;
        this.openSnackBar(error.message, 'Ok');
      }
    });
  }

  delete() {
    if (this.data.code === 'edit-channel') {
      const dialogRef = this.dialog.open(ChannelsDialogComponent, {
        data: {
          code: 'delete-channel',
          channel: this.data.channel,
          logosLibrary: this.data.logosLibrary
        }
      });
      dialogRef.afterClosed().subscribe((result: string) => {
        if (result === 'channel-deleted') {
          this.closeDialog('channel-deleted');
        }
      });
    } else {
      this.loadingData = true;
      const id = this.data.channel.id;
      this.channelService.deleteChannel(id).subscribe({
        next: () => {
          this.loadingData = false;
          this.closeDialog('channel-deleted');
        },
        error: (error) => {
          this.loadingData = false;
          this.openSnackBar(error.message, 'Ok');
        }
      });
    }
  }

  showErrorMessage(field: string) {
    return (
      this.channelForm.controls[field].errors &&
      this.channelForm.controls[field].touched
    );
  }

  disableSaveButton() {
    return !this.channelForm.valid;
  }

  setChannelInfo(channel: ChannelsResponse) {
    this.oldChannel = {
      name: channel.name,
      logo_id: channel.logo_id,
      stream_source: channel.stream_source
    };
    this.channelForm.get('name').setValue(channel.name);
    this.channelForm.get('stream_source').setValue(channel.stream_source);
    this.selectedLogo = this.data.logosLibrary.find(
      (logo) => logo.id === channel.logo_id
    );
    if (this.selectedLogo === undefined) {
      this.selectedLogo = this.data.logosLibrary.find(
        (logo) => logo.is_default
      );
    }
  }

  logoUpdated(logo: LogoLibraryResponse) {
    this.selectedLogo = logo;
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 6000
    });
  }

  addressValidator(control: AbstractControl): ValidationErrors {
    const supportedVideoProtocols = [
      'http',
      'https',
      'udp',
      'rtp',
      'rtsp',
      'rtmp',
      'mms'
    ];
    const streamSource: string = control.value;
    if (!streamSource) return { invalidUrl: true };
    if (streamSource.indexOf('://') == -1) {
      return { invalidUrl: true };
    }
    const [protocol, address] = streamSource.split('://');
    if (!protocol) {
      return { invalidUrl: true };
    }
    const isSupported = supportedVideoProtocols.includes(protocol);
    if (!isSupported) {
      return { invalidUrl: true };
    }
    if (['udp', 'rtp'].includes(protocol) && !address.startsWith('@')) {
      return { invalidUrl: true };
    }
    return null;
  }

  get readOnlyMode() {
    return this.data.code === 'edit-channel' && this.data.channel.is_external;
  }

  openEditProgramGuideDialog(channel: ChannelsResponse) {
    this.epgDialog
      .openDialog({
        content: EpisodesCalendarViewComponent,
        title: 'Edit Channel EPG',
        data: {
          channel,
          episodes: [],
          logosLibrary: this.data.logosLibrary
        }
      })
      .afterClosed()
      .subscribe({
        next: (value) => {
          if (value === null) {
            return;
          }
          this.openSnackBar('Channel EPG updated successfully', 'Ok');
        },
        error: (error) => {
          this.openSnackBar(error.message, 'Ok');
        }
      });
  }
}
